/**
 * audit.ts — shared writer for the append-only `audit_logs` table.
 *
 * audit_logs columns (the DB contract):
 *   - resource_type (required), action (required)
 *   - actor_id?    (uuid, FK profiles) — resolved here from the current session
 *   - resource_id? (text), metadata? (jsonb), ip_address? (omitted — see below)
 *   - id + created_at auto-fill (DB defaults). No workspace_id (audit is
 *     role-scoped; super_admin reads).
 *
 * Design: audit is a NON-CRITICAL side-effect. `writeAudit` is defensive — it
 * NEVER throws and never blocks/rolls back the caller's operation. Callers run
 * it AFTER their mutation succeeds, fire-and-forget; they don't need to guard it.
 *
 * ip_address is intentionally omitted: the browser can't reliably get the real
 * client IP, so the column stays null (a server-side writer could populate it).
 *
 * @module lib/audit
 */

import { supabase } from './supabase';

export interface WriteAuditInput {
  /** Table or domain acted on — e.g. 'invoices', 'leave_requests'. */
  resource_type: string;
  /** Action name — e.g. 'created', 'updated', 'deleted', 'status_changed'. */
  action: string;
  /** PK of the affected row. */
  resource_id?: string | null;
  /** Before/after snapshot or extra details (jsonb). */
  metadata?: Record<string, any> | null;
}

/**
 * Append one row to `audit_logs`. Defensive by design: any failure (auth,
 * network, RLS, schema) is swallowed and logged to console — it must never
 * surface to the user or roll back the operation being logged.
 */
export async function writeAudit(input: WriteAuditInput): Promise<void> {
  try {
    // Resolve actor_id from the locally-cached session — same pattern as
    // apiClient.ts (no extra network round-trip). If it can't be resolved,
    // still write the row with actor_id null (a row without an actor beats
    // no row at all).
    let actorId: string | null = null;
    try {
      // eslint-disable-next-line no-restricted-syntax -- auth session read, not a data query; no swallowable fetch error
      const { data } = await supabase.auth.getSession();
      actorId = data.session?.user?.id ?? null;
    } catch {
      actorId = null;
    }

    // id + created_at auto-fill via DB defaults; ip_address omitted (null).
    const { error } = await supabase.from('audit_logs').insert({
      resource_type: input.resource_type,
      action:        input.action,
      actor_id:      actorId,
      resource_id:   input.resource_id ?? null,
      metadata:      input.metadata ?? null,
    });
    if (error) {
      console.warn('[audit] writeAudit insert failed (non-fatal):', error.message);
    }
  } catch (err) {
    // Belt-and-suspenders: nothing in here may ever throw to the caller.
    console.warn('[audit] writeAudit threw (non-fatal):', err);
  }
}
